import { useFormContext, useWatch } from "react-hook-form";
import AttributeSchemaEditorUtils, { AttributeSchemaFormType } from "../AttributeSchemaEditorUtils";
import { Alert, Box, Button, Collapse, Stack, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ExpandMore, Square } from "@mui/icons-material";
import { eLynxObjectTypeEnum } from "../../../common/enums/eLynxObjectType";
import { getObjectTypeName } from "../../../common/utilities/GetObjectTypeName";
import { AttributeSchemaTypeEnum } from "../../../services/attribute-data-service/Models";
import { AttributeSchemaUsageCard } from "../../../views/attribute-management-view/attribute-schema-list/AttributeSchemaUsageCard";
import { ContentContainer } from "../../content-container/ContentContainer";
import { ContentSection } from "../../content-section/ContentSection";
import NoData from "../../no-data/NoData";


export default function AttibuteSchemaSummary() {
    
    const [usageExpanded, setUsageExpanded] = useState(true);
    const { t } = useTranslation();
    const {
        //getValues,
        //watch,
        //trigger,
        control,
        //reset,
        //setValue
    } = useFormContext<AttributeSchemaFormType>();    
   
    const attributeSchema = useWatch({ control: control }) as AttributeSchemaFormType;    
    const theme = useTheme();


    const devices = attributeSchema.devices.filter(o => o.selected);
    const wells = attributeSchema.wells.filter(o => o.selected)
    const tanks = attributeSchema.tanks.filter(o => o.selected)
    const tankBatteries = attributeSchema.tankBatteries.filter(o => o.selected)

    const deviceUsage = AttributeSchemaEditorUtils.mapAttributesToUsage(attributeSchema.schemaType, devices);
    const wellUsage = AttributeSchemaEditorUtils.mapAttributesToUsage(attributeSchema.schemaType, wells);
    const tankUsage = AttributeSchemaEditorUtils.mapAttributesToUsage(attributeSchema.schemaType, tanks);
    const tankBatteryUsage = AttributeSchemaEditorUtils.mapAttributesToUsage(attributeSchema.schemaType, tankBatteries);

    const totalUsageCount = deviceUsage.length + wellUsage.length + tankUsage.length + tankBatteryUsage.length;

    const usageCounts = AttributeSchemaEditorUtils.getUsageCounts(attributeSchema);

    function renderProperty(name: string, value: string | number) {

        return (
            <Stack
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flex: '0 0 auto',
                    marginBottom: 1
                }}>
                <Box
                    sx={{
                        flexBasis: '50%',
                        maxWidth: 300
                    }}>
                    {name}:
                    
                </Box>
                <Box sx={{
                    flexBasis: '50%'
                }}>
                    {value}
                </Box>
            </Stack>
        );
    }

   return (
       <ContentContainer>
           <ContentSection sx={{
               padding: 1,
               overflowY: 'auto'
           }}>


               <Typography variant="h6" sx={{
                   marginBottom: 1
               }}>
                   {t('attributeSchema.attributeProperties')}
               </Typography>

               {renderProperty(t('attributeSchema.type'), AttributeSchemaEditorUtils.getSchemaTypeText(attributeSchema.schemaType) )}
               {renderProperty(t('attributeSchema.groupBy'), attributeSchema.allowGroupBy ? 'True' : 'False' )}


               {(attributeSchema.schemaType === AttributeSchemaTypeEnum.PredefinedValue ||
               attributeSchema.schemaType === AttributeSchemaTypeEnum.PredefinedValueAndFreeformText) && 
                   //  <AttributeSchemaValuesCard />
                   <Stack
                       direction='column'
                       sx={{
                           display: 'flex',
                           marginBottom: 1
                       }}>
                       <Stack
                           sx={{
                               display: 'flex',
                               flexDirection: 'row',
                               flex: '0 0 auto',
                               marginBottom: 1
                           }}>
                           {t('attributeSchema.attributeOptions')}:
                       </Stack>

                       {
                           attributeSchema.valueOptions.map((o, index) =>

                               <Typography key={index} 
                                   variant='body1' 
                                   component='div' 
                                   noWrap={false}  
                                   sx={{
                                       color: theme.palette.grey[600],
                                       marginLeft: theme.spacing(3),
                                       '& + &': {
                                           marginTop: 1
                                       }
                               }}>
                                   <Square sx={{
                                       fontSize: '6px'
                                   }}/>
                                   
                                   <Box component='span' sx={{
                                       marginLeft: 2
                                   }} >
                                       {o.value}
                                   </Box>
                               </Typography>

                           )
                       }                                          



                   </Stack>
               }

               {usageCounts.added > 0 && 
           
                   <Alert severity="info"
                       sx={{
                           marginTop: 1,
                           marginRight: 2
                   }}>
                       {t('attributeSchema.addedCount', {count: usageCounts.added})}
                   </Alert>

               }

               {usageCounts.updated > 0 && 
                   <Alert severity="info"
                       sx={{
                           marginTop: 1,
                           marginRight: 2
                   }}>
                       {t('attributeSchema.updatedCount', {count: usageCounts.updated})}
                   </Alert>
               }

               {usageCounts.deleted > 0 && 
                   <Alert severity="info"
                   sx={{
                       marginTop: 1,
                       marginRight: 2
                   }}>
                       {t('attributeSchema.deletedCount', {count: usageCounts.deleted})}
                   </Alert>

               }


               { totalUsageCount > 0 ? 
               <>
                   <Button
                           sx={{
                               color: "text.primary"
                           }}
                           variant="text"
                           onClick={() => { setUsageExpanded(!usageExpanded)}
                           }
                           style={{ marginLeft: "auto" }}
                           endIcon={<ExpandMore sx={{
                               transform: !usageExpanded ? "rotate(0deg)" : "rotate(180deg)",
                               transition: theme.transitions.create('transform', {
                                   duration: theme.transitions.duration.shortest,
                               })
                           }}
                           />}
                       >
                           {t('usageWithCount', {count: totalUsageCount})}
                       </Button>


                   <Collapse in={usageExpanded} timeout="auto">

                       {deviceUsage.length > 0 && 
                           <AttributeSchemaUsageCard 
                               title={getObjectTypeName(eLynxObjectTypeEnum.Device, true)} 
                               items={deviceUsage}
                           />
                       }

                       {tankUsage.length > 0 && 
                           <AttributeSchemaUsageCard 
                               title={getObjectTypeName(eLynxObjectTypeEnum.Tank, true)} 
                               items={tankUsage}
                           />
                       }

                       {tankBatteryUsage.length > 0 && 
                           <AttributeSchemaUsageCard 
                               title={getObjectTypeName(eLynxObjectTypeEnum.TankBattery, true)} 
                               items={tankBatteryUsage}
                           />
                       }

                       {wellUsage.length > 0 && 
                           <AttributeSchemaUsageCard 
                               title={getObjectTypeName(eLynxObjectTypeEnum.Well, true)} 
                               items={wellUsage}
                           />
                       }

                   </Collapse>
                   </>
                   :
                   <Box sx={{margin: 1, marginLeft: 0}}>
                       <NoData title=''
                           text={t('attributeSchema.noUsage')}
                       />

                   </Box>
                   }




           </ContentSection>
       </ContentContainer>
   );
  }
